import { getApiUrl } from './apiConfig';

class CreditService {
  constructor() {
    this.authContext = null;
    this.debugLogging = process.env.NODE_ENV !== 'production';
  }

  setAuthContext(authContext) {
    this.authContext = authContext;
  }

  /**
   * Get current user's credit balance
   * @returns {Promise<number>} Credit balance
   */
  async makeAuthenticatedRequest(path, options = {}) {
    try {
      if (!this.authContext) {
        throw new Error('Auth context not set');
      }

      const { currentUser, getToken } = this.authContext;
      if (!currentUser) {
        throw new Error('No authenticated user');
      }

      // Get initial token
      const token = await getToken();
      if (!token) {
        throw new Error('Failed to get authentication token');
      }

      const url = getApiUrl(path);
      if (this.debugLogging) {
        console.log('Making authenticated request to:', url);
      }

      // Make request with token
      const response = await fetch(url, {
        ...options,
        headers: {
          ...options.headers,
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      // If unauthorized, try refreshing token
      if (response.status === 401) {
        if (this.debugLogging) console.log('Token expired, refreshing...');
        const newToken = await getToken(true);
        if (!newToken) {
          throw new Error('Failed to refresh authentication token');
        }

        // Retry with new token
        if (this.debugLogging) console.log('Retrying with new token');
        const retryResponse = await fetch(url, {
          ...options,
          headers: {
            ...options.headers,
            'Authorization': `Bearer ${newToken}`,
            'Content-Type': 'application/json'
          },
          credentials: 'include'
        });

        if (!retryResponse.ok) {
          throw new Error(`Request failed: ${retryResponse.status}`);
        }

        return retryResponse;
      }

      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
      }

      return response;
    } catch (error) {
      console.error('Request error:', error);
      throw error;
    }
  }

  /**
   * Get user's credit balance
   * @returns {Promise<number>} Credit balance
   */
  async getCredits() {
    try {
      if (this.debugLogging) console.log('Getting credit balance');
      const response = await this.makeAuthenticatedRequest('/api/credits');
      const data = await response.json();
      if (this.debugLogging) console.log('Received credit balance:', data.balance);
      return data.balance;
    } catch (error) {
      console.error('Error getting credit balance:', error);
      throw error;
    }
  }

  /**
   * Get the balance together with whether it is currently spendable.
   * Credits only convert books while a subscription is active, so a balance on
   * its own is not enough to render honestly.
   * @returns {Promise<{balance: number, subscriptionActive: boolean}>}
   */
  async getCreditStatus() {
    const response = await this.makeAuthenticatedRequest('/api/credits');
    const data = await response.json();
    const lots = Array.isArray(data.lots) ? data.lots : [];
    return {
      balance: Number(data.balance) || 0,
      // Older backends answered {balance} only; treat a missing flag as active
      // so an out-of-date API can never falsely tell a paying user they lapsed.
      subscriptionActive: data.subscriptionActive !== false,
      // Bucket breakdown. An older backend sends none of this: allowance falls
      // back to the whole balance and topups to 0, which renders as a plain
      // total with no breakdown rather than as zeroes.
      allowance: data.allowance === undefined ? null : Number(data.allowance) || 0,
      topups: data.topups === undefined ? null : Number(data.topups) || 0,
      allowancePeriodEnd: data.allowancePeriodEnd || null,
      nextExpiry: data.nextExpiry
        ? {
            amount: Number(data.nextExpiry.amount) || 0,
            expiresAt: data.nextExpiry.expiresAt || null
          }
        : null,
      lots: lots.map((lot) => ({
        amount: Number(lot.amount) || 0,
        expiresAt: lot.expiresAt || null
      }))
    };
  }

  /**
   * Deduct credits from user's balance
   * @param {number} amount Amount to deduct
   * @returns {Promise<boolean>} True if successful
   */
  async deductCredits(amount) {
    try {
      if (this.debugLogging) {
        console.log('Deducting credits:', amount);
        console.log('Making deduct credits request with body:', { amount });
      }
      const response = await this.makeAuthenticatedRequest('/api/credits/deduct', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(amount)  // Send amount directly, not as an object
      });
      if (this.debugLogging) console.log('Deduct credits response status:', response.status);
      const data = await response.json();
      if (this.debugLogging) console.log('Deduct credits response data:', data);
      return data.success;
    } catch (error) {
      console.error('Error deducting credits:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        response: error.response
      });
      throw error;
    }
  }

  /**
   * Add credits to user's balance
   * @param {number} amount Amount to add
   * @returns {Promise<boolean>} True if successful
   */
  async addCredits(amount) {
    try {
      console.log('Adding credits:', amount);
      const response = await this.makeAuthenticatedRequest('/api/credits/add', {
        method: 'POST',
        body: JSON.stringify(amount)  // Send amount directly, matching backend Body(float)
      });
      const data = await response.json();
      console.log('Add credits result:', data);
      return data.success;
    } catch (error) {
      console.error('Error adding credits:', error);
      throw error;
    }
  }

  /**
   * Check if user has enough credits
   * @param {number} required Required credits
   * @returns {Promise<boolean>} True if user has enough credits
   */
  async checkCredits(required) {
    try {
      const balance = await this.getCredits();
      return balance >= required;
    } catch (error) {
      console.error('Error checking credits:', error);
      throw error;
    }
  }

  /**
   * Calculate word count from character count
   * @param {number} charCount Number of characters
   * @returns {number} Estimated word count
   */
  getWordCount(charCount) {
    // Average word length in English is ~5 characters
    return Math.ceil(charCount / 5);
  }

  /**
   * Calculate required credits based on total word count
   * @param {number} wordCount Number of words
   * @returns {number} Required credits
   */
  calculateRequiredCredits(wordCount) {
    const normalizedWords = Math.max(0, Number(wordCount) || 0);
    // 1 credit per 100k words
    return Math.max(1, Math.ceil(normalizedWords / 100000));
  }

  /**
   * Get human-readable size description
   * @param {number} charCount Number of characters
   * @returns {string} Size description
   */
  getSizeDescription(charCount) {
    const wordCount = this.getWordCount(charCount);
    if (wordCount < 1000) {
      return `${wordCount} words`;
    } else if (wordCount < 1000000) {
      return `${(wordCount / 1000).toFixed(1)}K words`;
    } else {
      return `${(wordCount / 1000000).toFixed(1)}M words`;
    }
  }
}

const creditService = new CreditService();
export default creditService;
